import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useListFiltersInUrl } from '../../../../../hooks';
import { jokesColumns } from '../../../../../utils/consts/columnDefs/contentColumns';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

import { useGetJokesQuery } from '../../../../../services/endpoints/content/joke';
import { useGetJokeCategoriesQuery } from '../../../../../services/endpoints/content/jokeCategory';
import { User } from '../../../../../services/endpoints/people/user';
import { currentUserHasRole } from '../../../../../services/helper';
import { useAppDispatch, useAppSelector } from '../../../../../services/hooks';
import { selectJoke, setJokes } from '../../../../../services/slicers/jokeSlice';
import { JokeCategory } from '../../../../../types/content.type';
import { isAdmin } from '../../../../../utils/consts';

const defaultScope = 'all';

export const userCanEditCreateDeleteView_Joke = (user: User | null) => {
  if (user?.isAdmin && user?.isSuper) {
    return true;
  }
  if (user?.isWebContentEditor || user?.isSupremeAdmin || user?.isGlobal) {
    return true;
  }

  return false;
};

export const JokesView = () => {
  const [filters, setFilters] = useListFiltersInUrl(
    {
      scope: defaultScope,
      data: { page: 1, entries: 10 }
    },
    { omitScopeFromUrl: true }
  );
  const [jokeCategories, setJokeCategories] = useState<JokeCategory[]>([]);
  const { data, error, isLoading, isFetching } = useGetJokesQuery(filters);
  const { data: dataJokeCategories, error: jokeCategoriesError } = useGetJokeCategoriesQuery();
  const navigate: NavigateFunction = useNavigate();
  const isAdminUser = currentUserHasRole(isAdmin);
  const jokes = useAppSelector(selectJoke);
  const dispatch = useAppDispatch();
  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'content',
      label: 'Content',
      value: getFilterValue(filters.data?.filterBy, 'content')
    },
    {
      name: 'Joke Category',
      element: (
        <FormSelect
          name="joke_category_id"
          label="Joke Category"
          options={jokeCategories.map((jokeCategory: JokeCategory) => ({
            value: jokeCategory.id,
            label: jokeCategory.name
          }))}
          selected_Value={jokeCategories
            .map((jokeCategory: JokeCategory) => ({
              value: jokeCategory.id,
              label: jokeCategory.name
            }))
            .find(
              (option) =>
                String(option.value) === getFilterValue(filters.data?.filterBy, 'joke_category_id')
            )}
        />
      )
    }
  ];

  useEffect(() => {
    if (data?.results) {
      dispatch(setJokes(data.results));
    }
    if (dataJokeCategories?.results) {
      setJokeCategories(dataJokeCategories.results);
    }
  }, [data, dataJokeCategories]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`Jokes`}
        columns={isAdminUser ? jokesColumns : jokesColumns.slice(1)}
        data={jokes}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        // indexDownloadPath="jokes"
        filters={filters?.data?.filterBy}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          <SimpleButton
            variant="sfc-blueberry"
            height={40}
            truncate={false}
            onClick={() => navigate('/admin/content/jokes/new')}
          >
            New Joke
          </SimpleButton>
        }
      />
    </div>
  );
};
